import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import useFormName from '../../../../Hooks/useFormName';
import ErrorMessage from '../ErrorMessage';
import TextArea, { TextAreaProps } from '../TextArea';
import TopFieldContent from '../TopFieldContent';
import CharactersCount from '../CharactersCount/CharactersCount';
import InputWrapper from '../InputWrapper/InputWrapper';
import useTranslation from '../../../../Hooks/useTranslation';
import { Position } from '../../../../Modules/Base/Enums/Position.enum';

interface TextAreaFieldProps extends TextAreaProps {
	name: string;
	label?: string;
	className?: string;
	isRequired?: boolean;
	hideRequired?: boolean;
	charactersLimit?: number;
	labelStyle?: object;
	disabled?: boolean;
	showCharCounter?: boolean;
	isWrapped?: boolean;
	placeholder?: string;
	style?: React.CSSProperties;
	captureAudio?: {
		allow: boolean;
		position: Position;
	};
}

const TextAreaField = ({
	name,
	label,
	className,
	isRequired,
	hideRequired,
	labelStyle,
	disabled,
	charactersLimit,
	isWrapped,
	placeholder,
	showCharCounter = false,
	style,
	...textAreaProps
}: TextAreaFieldProps) => {
	const { fieldName } = useFormName({ name });
	const { t } = useTranslation();

	const { control } = useFormContext();

	const {
		field,
		fieldState,
		fieldState: { error },
	} = useController({
		control,
		name: fieldName,
		defaultValue: '',
	});

	const component = (
		<div className={className}>
			{label && (
				<TopFieldContent
					label={label}
					isRequired={isRequired}
					hideRequired={hideRequired}
					labelStyle={labelStyle}
				/>
			)}
			<TextArea
				hasError={Boolean(error?.message)}
				disabled={disabled}
				charactersLimit={charactersLimit}
				placeholder={t(placeholder)}
				style={style}
				{...textAreaProps}
				{...field}
			/>
			{error?.message && <ErrorMessage tx={error?.message} />}
			{charactersLimit && showCharCounter && (
				<CharactersCount limit={charactersLimit} value={field.value} />
			)}
		</div>
	);

	if (isWrapped) {
		return <InputWrapper>{component}</InputWrapper>;
	}

	return component;
};

export default TextAreaField;
